import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, AlertCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import AuthShell from "@/pages/auth/AuthShell";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  // After login, return to the page user came from (set by ProtectedRoute or
  // the Sign-in link). Default is the marketing homepage so users can keep
  // exploring the site instead of being slammed into the CRM dashboard.
  const from = loc.state?.from || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Signed in — welcome back.");
      navigate(from, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthShell
      title={
        <>
          Welcome <span className="italic">back.</span>
        </>
      }
      subtitle="Sign in to continue to your CRM workspace."
      footer={
        <>
          New to Heritex?{" "}
          <Link
            to="/signup"
            data-testid="link-to-signup"
            className="text-[#1A1A1A] underline underline-offset-4 hover:no-underline"
          >
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} data-testid="login-form" className="space-y-2">
        <div>
          <label className="caption block mb-1">Email</label>
          <input
            data-testid="login-email"
            type="email"
            autoComplete="email"
            required
            className="editorial-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            placeholder="you@company.com"
          />
        </div>

        <div>
          <label className="caption block mb-1">Password</label>
          <div className="relative">
            <input
              data-testid="login-password"
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              required
              className="editorial-input pr-8"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              className="absolute right-0 top-1/2 -translate-y-1/2 p-1.5 text-[#6B6A68] hover:text-[#1A1A1A]"
              aria-label="Toggle password visibility"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {error && (
          <div
            data-testid="login-error"
            className="mt-4 flex items-start gap-2 text-sm text-[#B03A2E]"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          data-testid="login-submit"
          type="submit"
          disabled={submitting}
          className="mt-10 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm font-medium hover:bg-[#333] transition-all disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Sign in"}
          <ArrowUpRight className="w-4 h-4" />
        </button>
      </form>
    </AuthShell>
  );
}
